// Connections in the UI: their edits and the undo step around them, the Connect
// tool's draft ends and snap, pipes and their bends, picking a connection, its
// card, the part's Connections tab and the list of them all.
// docs/claude/connections.md.
import { productName } from '../../shared/names';
import {
  pipeOuter,
  pipeTypeName,
} from '../../shared/pipeTypes';
import {
  type Connection,
  type ConnectionType,
  type ConnEnd,
  type PipeType,
  type Polygon,
  type ProductInstance,
  type Vec2,
} from '../../shared/types';
import {
  connectionLength,
  connectionRunLength,
  isComputedLength,
} from '../cableLength';
import {
  bendDragEdit,
  draftBendPoint,
  planRouteHit,
} from '../connDrag';
import {
  applyConnEdit,
  CONN_DEFAULTS,
  type ConnEdit,
  type ConnEditContext,
  connEditRefusal,
  type ConnSpec,
  type ConnSticky,
  followPipeType,
  newConnection,
} from '../connEdits';
import { type ConnPick } from '../connPick';
import {
  CONN_HOST_KINDS,
  connDiscipline,
  connectionShown,
  connectionTouches,
  type ConnGeom,
  connGeom,
  type ConnHostKind,
  connHostObjects,
  pipeEndZ,
  portName,
} from '../connections';
import {
  beginGesture,
  commitGesture,
  flashStatus,
  invalidate,
  newId,
  objUnpickable,
  pushHistory,
  renderProperties,
  state,
  vp,
} from './core';
import { armPick } from './armedPick';
import { distance } from '../geometry';
import { GRIP_HIT_PX } from '../grip';
import { relatedTo } from '../groups';
import { sectionHandleAt } from './handleWork';
import {
  getUiLang,
  tr,
} from '../i18n';
import { curLang } from './nameFields';
import {
  numberedInstanceNames,
  type RowNames,
  rowNames,
  showObjectRowMenu,
} from './partsTab';
import {
  pathPointInView,
  pipeTally,
} from '../pipes';
import {
  CONNECTION_COLORS,
  connTagAt,
} from '../render';
import {
  connectionRows,
  connectionSchedule,
  scheduleNumbers,
  scheduleStrings,
} from '../schedule';
import {
  sectionFrame,
  sectionFramePoint,
} from '../section';
import {
  connectionPath,
  DISCIPLINE_COLORS,
  disciplineSelect,
  editVertexSnapHit,
  hitCtx,
  hostRoomOf,
  planHits,
  refOutline,
  reverseSelectedConnection,
  selectRef,
  setCanvasHover,
  SNAP_PX,
  topFirst,
  zoomToPoints,
} from '../main';
import {
  buttonRow,
  infoRow,
  labeledField,
  numberRow,
  parseMeasure,
  sectionTitle,
  selectRow,
} from './panelRows';
import { pipeTypes } from './standards';
import { labelShown } from './scheduleTables';
import {
  type ObjRef,
  refKey,
} from '../pickStack';
import { inSectionView } from '../hitRules';

/** This module's state that main.ts reads and writes too. */
export const connectionUi = {
  // Connection tool: dragging a link from one part to another. `to` tracks the
  // cursor; on release over another part, a connection is created.
  /** One end being picked for a connection: the object it attaches to ('' = a free
   *  point), how it is glued (absent = that object's centre) and where it is now. */
  /** The connection being drawn: its start pick, its points (the start's, then
   *  each bend — the click-by-click draft polylineDraft.ts rules on), the pick
   *  under the cursor, and where a press began (a drag connects too). */
  connDraft: null as { from: ConnPick; pts: Vec2[]; hover: ConnPick; press: Vec2 | null } | null,
  /** The discipline a new connection is born with: the last one set. */
  lastConnType: 'electrical' as ConnectionType,
  /** A discipline was typed while this Connect tool is armed: it beats a port's own. */
  connTypeTyped: false,
  /** How the next connection runs — typed as an option while drawing, or set on a
   *  connection's card — so a run of right-angle links stays right-angled. */
  lastConnRoute: 'straight' as 'straight' | 'orthogonal',
};

/** The connection `c` resolved (connections.ts `connGeom`): its routes in plan
 *  and in 3D, its heights, its pipe. */
export function connGeomOf(c: Connection): ConnGeom | null {
  return connGeom(state.drawing, c);
}

/** The pipe types a connection may be made of: the catalogue's, then any the
 *  drawing carries that the catalogue no longer has. */
export function pipeTypeChoices(): PipeType[] {
  const out = [...pipeTypes];
  for (const t of state.drawing.pipeTypes ?? []) if (!out.some((x) => x.id === t.id)) out.push(t);
  return out;
}

/** One edit of a connection (connEdits.ts), against the drawing and the pipe
 *  types it may be made of. No history: the caller records it. */
export function connEdit(c: Connection, edit: ConnEdit): boolean {
  return applyConnEdit(connEditCtx(), c, edit);
}
export function connEditCtx(): ConnEditContext {
  return { drawing: state.drawing, pipeTypes: pipeTypeChoices() };
}

/** The choices a new connection takes (connEdits.ts `newConnection`): the last
 *  discipline, routing and pipe used, and whether the discipline was typed for
 *  the line being drawn. */
export function connSticky(): ConnSticky {
  return { type: connectionUi.lastConnType, route: connectionUi.lastConnRoute, pipe: lastConnPipe, typed: connectionUi.connTypeTyped };
}
export const newConnectionId = (): string => newId('con');

/** The two picks a connection runs between, as its ends: an object's (where on
 *  it), or a free point where the pick stands; the discipline of a port at
 *  either, the first's first. */
export function draftEnds(from: ConnPick, to: ConnPick): Pick<ConnSpec, 'from' | 'fromEnd' | 'to' | 'toEnd' | 'type'> {
  const end = (k: ConnPick): ConnEnd | undefined => (k.id ? k.end : { p: k.p });
  return { from: from.id, fromEnd: end(from), to: to.id, toEnd: end(to), type: from.type ?? to.type };
}

/** A connection added from a panel (a shape's, a part's Connections tab):
 *  born as the Connect tool's are (`newConnection`), one undo step. */
function addConnection(spec: ConnSpec): void {
  pushHistory();
  state.drawing.connections.push(newConnection(connEditCtx(), spec, connSticky()));
  renderProperties();
  invalidate();
}

/** A connection's Physical select: a pipe type, or none (a plain line again).
 *  The next connection drawn is made of the same. */
function setConnectionPipe(c: Connection, id: string): void {
  pushHistory();
  connEdit(c, { kind: 'pipe', type: id || null });
  lastConnPipe = id;
  renderProperties();
  invalidate();
}

/** A type saved in Tools ▸ Pipe types: the open drawing's copy follows, and so
 *  does every pipe of it still at the old copy's diameter and wall. */
export function refreshDrawingPipeType(t: PipeType): void {
  if (!state.drawing.pipeTypes?.some((x) => x.id === t.id)) return;
  pushHistory();
  followPipeType(state.drawing, t);
  renderProperties();
  invalidate();
}

/** A free bend clicked while a connection is drawn: a pipe's squared to the
 *  point before it (connDrag.ts `draftBendPoint`; Alt: free). */
export function connDraftSnap(p: Vec2, free: boolean): Vec2 {
  const d = connectionUi.connDraft, t = lastConnPipe ? pipeTypeChoices().find((x) => x.id === lastConnPipe) : undefined;
  return !d || free ? p : draftBendPoint(d.pts[d.pts.length - 1], p, t ? { flexible: !!t.flexible } : null, SNAP_PX / vp.scale);
}

/** Bend `i` of `c` dragged on the plan to `world` — by its grip, or picked up
 *  by a double-click — where connDrag.ts's ONE rule puts it: the plan's snaps
 *  (an object snap wins; else alignment with its other points and the objects
 *  near, 45° / square from the point before, the grid), then pulled square — a
 *  pipe's at 45° too — with the points either side. Alt, or a hose: free. */
export function planBendDrag(c: Connection, i: number, world: Vec2, free: boolean): void {
  const g = connGeomOf(c);
  if (!g || !c.waypoints?.[i]) return;
  const others = g.ctrl.filter((_, k) => k !== i + 1).map(({ x, y }) => ({ x, y }));
  const unsnapped = free || !!g.pipe?.flexible;
  const snap = unsnapped ? null : editVertexSnapHit(world, others, c.id, { x: g.ctrl[i].x, y: g.ctrl[i].y }, false);
  if (unsnapped) state.editGuide = null;
  connEdit(c, bendDragEdit({ g, i, raw: world, snap, tol: SNAP_PX / vp.scale }));
}

/** A double-click picks up bend `index` of `conn` (`isNew`: just added there):
 *  an armed pick. It follows the pointer by planBendDrag's rule until the next
 *  click drops it (one undo step: the gesture began with the double-click);
 *  cancelled, a new bend goes and a moved one goes back to `orig`. */
export function pickUpBend(conn: Connection, index: number, isNew: boolean, orig: Vec2): void {
  armPick({
    kind: 'waypoint',
    click: () => { commitGesture(); },
    move: (e, sp) => {
      const c = state.drawing.connections.find((x) => x.id === conn.id);
      if (c?.waypoints?.[index]) { planBendDrag(c, index, vp.screenToWorld(sp), e.altKey); invalidate(); }
      return true;
    },
    cancel: () => {
      const c = state.drawing.connections.find((x) => x.id === conn.id);
      if (c) connEdit(c, isNew ? { kind: 'bendRemove', i: index } : { kind: 'bendMove', i: index, p: orig });
      commitGesture(); // state restored to the gesture's start → records nothing
    },
  });
}

/** The selected connection: its handles show in 3D, and a pipe's in the section
 *  views too. */
export function selectedConnection(): Connection | null {
  return (state.selectedConnId ? state.drawing.connections.find((x) => x.id === state.selectedConnId) : undefined) ?? null;
}
/** The selected connection when it is a pipe. */
function selectedPipe(): Connection | null {
  const c = selectedConnection();
  return c?.pipe ? c : null;
}

/** Double-click on the selected pipe where a section view shows it: a bend
 *  there, at the height it is seen at. True when it took the click. */
export function addPipeBendInView(world: Vec2): boolean {
  const c = selectedPipe();
  const r = c ? connGeomOf(c)?.path : null;
  if (!c || !r) return false;
  const ctx = hitCtx();
  for (const sec of state.drawing.sections || []) {
    if (objUnpickable(sec.id) || !inSectionView(sec, world, ctx)) continue;
    const { u, n } = sectionFrame(sec);
    const hit = pathPointInView(r.pts, sec.a, u, n, sectionFramePoint(sec, world), GRIP_HIT_PX / vp.scale);
    if (!hit) continue;
    pushHistory();
    connEdit(c, { kind: 'bendAdd', at: r.leg[hit.seg], p: hit.p });
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

/** Right-click on a bend's handle in a section view: the bend goes (its ends
 *  stay). True when it took the click. */
export function removePipeBendInView(sp: Vec2): boolean {
  const m = sectionHandleAt(sp), c = selectedPipe();
  if (!m || !c?.waypoints || !m.hd.key.startsWith('bend:')) return false;
  const i = Number(m.hd.key.slice(5)) - 1;
  if (!(i >= 0 && i < c.waypoints.length)) return false;
  pushHistory();
  connEdit(c, { kind: 'bendRemove', i });
  renderProperties();
  invalidate();
  return true;
}

/** A schematic connection's 3D figures, its handles' panel rows: each end's
 *  height and the run's (cleared: back to what it takes by itself). */
function renderConn3dRows(body: HTMLElement, c: Connection): void {
  const run = connGeomOf(c)?.run;
  if (!run) return;
  body.appendChild(sectionTitle('In 3D'));
  body.appendChild(connEndHeightRow(c, 'from'));
  body.appendChild(numberRow('Run height (mm)', Math.round(run.z), (x) => {
    pushHistory(); connEdit(c, { kind: 'runZ', z: x }); renderProperties(); invalidate();
  }, {
    commit: true,
    onClear: c.runZ != null ? () => { pushHistory(); connEdit(c, { kind: 'runZ', z: null }); renderProperties(); invalidate(); } : undefined,
  }));
  body.appendChild(connEndHeightRow(c, 'to'));
}

/** A connection end's height row (In 3D, a pipe's): its own, else what it takes
 *  by itself (`pipeEndZ`); cleared, back to that. Each change one undo step. */
function connEndHeightRow(c: Connection, which: 'from' | 'to'): HTMLElement {
  const i = which === 'from' ? 0 : (c.waypoints?.length ?? 0) + 1;
  const own = (which === 'from' ? c.fromEnd : c.toEnd)?.z;
  const edit = (z: number | null): void => { pushHistory(); connEdit(c, { kind: 'pointZ', i, z }); renderProperties(); invalidate(); };
  return numberRow(which === 'from' ? 'From height (mm)' : 'To height (mm)', Math.round(pipeEndZ(state.drawing, c, which)), (x) => edit(x), {
    commit: true,
    onClear: own != null ? () => edit(null) : undefined,
  });
}

/** The panel rows of a connection's pipe: what it is made of, its diameter and
 *  wall (the type's, editable), its ends' and bends' heights, and its tally. */
function renderPipeRows(body: HTMLElement, c: Connection): void {
  body.appendChild(sectionTitle('Pipe'));
  const lang = curLang();
  const opts: Array<readonly [string, string]> = [['', 'None'], ...pipeTypeChoices().map((t) => [t.id, pipeTypeName(t, lang)] as const)];
  body.appendChild(selectRow('Physical', opts, c.pipe?.type ?? '', (v) => setConnectionPipe(c, v)));
  const pipe = c.pipe;
  const g = connGeomOf(c);
  if (!pipe) return;
  const t = g?.pipe?.type;
  const fig = (label: string, v: number, size: (x: number) => ConnEdit): HTMLElement =>
    numberRow(label, v, (x) => {
      if (x === v || connEditRefusal({ drawing: state.drawing }, c, size(x))) { renderProperties(); return; }
      pushHistory(); connEdit(c, size(x)); renderProperties(); invalidate();
    }, { commit: true });
  body.appendChild(fig('Internal diameter (mm)', pipe.inner, (x) => ({ kind: 'pipeSize', inner: x })));
  body.appendChild(fig('Pipe wall (mm)', pipe.wall, (x) => ({ kind: 'pipeSize', wall: x })));
  body.appendChild(infoRow('Outer diameter (mm)', String(Math.round(pipeOuter(pipe) * 10) / 10)));
  if (t) body.appendChild(infoRow('Slope (%)', String(t.slope)));
  // Heights: an end's own, else half way up its object (cleared: back to that).
  body.appendChild(connEndHeightRow(c, 'from'));
  const ctrl = g?.ctrl ?? [];
  (c.waypoints ?? []).forEach((_, i) => {
    body.appendChild(numberRow(tr('Bend {n} height (mm)').replace('{n}', String(i + 1)), Math.round(ctrl[i + 1]?.z ?? 0), (x) => {
      pushHistory(); connEdit(c, { kind: 'pointZ', i: i + 1, z: x }); renderProperties(); invalidate();
    }, { commit: true }));
  });
  body.appendChild(connEndHeightRow(c, 'to'));
  if (!g?.pipe) return;
  const tally = pipeTally(g.pipe.geom);
  body.appendChild(infoRow('90° elbows', String(tally.bends90)));
  body.appendChild(infoRow('45° elbows', String(tally.bends45)));
  if (tally.other.length) body.appendChild(infoRow('Other bends', tally.other.map((a) => `${a}°`).join(', ')));
  body.appendChild(infoRow('Straight runs', String(tally.straights)));
  body.appendChild(infoRow('Straight length (m)', (tally.straightLength / 1000).toFixed(2)));
  body.appendChild(infoRow('Pipe length (m)', (tally.length / 1000).toFixed(2)));
}

/** Delete every connection with an end on one of `ids` — objects that were just
 *  deleted, so their connections go with them. Returns how many went. */
export function pruneConnectionsTo(ids: Iterable<string>): number {
  const set = new Set(ids);
  const before = state.drawing.connections.length;
  state.drawing.connections = state.drawing.connections.filter((c) => !connectionTouches(c, set));
  if (state.selectedConnId && !state.drawing.connections.some((c) => c.id === state.selectedConnId)) state.selectedConnId = null;
  return before - state.drawing.connections.length;
}

/** Say in the bar that a delete took `n` connections with it. */
export function flashConnectionsDeleted(n: number): void {
  if (n > 0) flashStatus(tr('{n} attached connection(s) deleted too.').replace('{n}', String(n)));
}

/** Every connection line near a world point (only while the schedule — and thus
 *  the lines — show), top of the stacking first; hidden and locked ones left out
 *  (`lockedToo`: the locked ones kept — seen, for the pick stack). */
export function connectionsAt(world: Vec2, tolPx = SNAP_PX, lockedToo = false): Array<{ conn: Connection; seg: number }> {
  const show = { schedule: state.showSchedule, filters: state.disciplineFilters };
  if (!show.schedule) return [];
  const tol = tolPx / vp.scale;
  const hits: Array<{ id: string; conn: Connection; seg: number }> = [];
  for (const c of state.drawing.connections) {
    if (!connectionShown(c, show)) continue; // hidden → not clickable
    // `seg` is the LEG (control point → next) the hit segment belongs to, so a bend
    // added there is inserted between the right two waypoints on an elbow route too.
    const g = connGeomOf(c), h = g && planRouteHit(g, world, tol);
    if (h) hits.push({ id: c.id, conn: c, seg: h.leg });
  }
  return topFirst(hits).filter((h) => lockedToo || !objUnpickable(h.id)).map(({ conn, seg }) => ({ conn, seg }));
}

/** Hit-test connection lines: the topmost one a press can take. */
export function hitConnection(world: Vec2, tolPx = SNAP_PX): { conn: Connection; seg: number } | null {
  return connectionsAt(world, tolPx)[0] ?? null;
}

/** The connection whose schedule tag is under a screen point, if a press may take it. */
export function pickableConnTagAt(sp: Vec2): string | null {
  return connTagAt(planHits, sp, 2, objUnpickable);
}

/** Hit-test a connection's mid-route vertex (for right-click delete). */
export function hitConnectionWaypoint(world: Vec2): { conn: Connection; index: number } | null {
  if (!state.showSchedule) return null;
  const tol = (SNAP_PX + 3) / vp.scale;
  for (let k = state.drawing.connections.length - 1; k >= 0; k--) {
    const c = state.drawing.connections[k];
    if (!c.waypoints || objUnpickable(c.id)) continue;
    for (let i = 0; i < c.waypoints.length; i++) {
      if (distance(world, c.waypoints[i]) <= tol) return { conn: c, index: i };
    }
  }
  return null;
}

/** The pipe type (`PipeType.id`) the next connection is made of, set on a
 *  connection's panel; '' = a plain line. */
let lastConnPipe = '';

/** Display name of a connection endpoint: a part's auto-numbered name, or a
 *  polygon/ellipse's (named or auto-numbered). */
export function connectionEndpointName(id: string): string {
  if (!id) return tr('free end');
  const n = rowNames();
  for (const k of CONN_HOST_KINDS) { const v = n[ROW_NAMES_OF[k]].get(id); if (v) return v; }
  return '?';
}
/** Where the Parts tab's names (`rowNames`) keep each host kind's: what the
 *  panel calls a connection's end on one. */
const ROW_NAMES_OF: { [K in ConnHostKind]: keyof RowNames } = { text: 'texts', instance: 'insts', polygon: 'polys', wall: 'walls', flooring: 'floors' };

/** One end of a connection as the panel names it: its object, and the door,
 *  heater wall or connection point it is glued to (`Sauna 1 · Door`). */
function connEndName(c: Connection, which: 'from' | 'to'): string {
  const id = which === 'from' ? c.from : c.to;
  const end = which === 'from' ? c.fromEnd : c.toEnd;
  const name = connectionEndpointName(id);
  const port = end?.port ? state.drawing.instances.find((i) => i.id === id)?.ports?.find((p) => p.id === end.port) : undefined;
  if (port) return `${name} · ${portName(port.name, tr)}`;
  if (end?.on?.kind === 'door') return `${name} · ${tr('Door')}`;
  if (end?.on?.kind === 'heaterWall') return `${name} · ${tr('Heater wall')}`;
  return name;
}

/** One editable connection card (schedule badge, endpoints, length, discipline,
 *  parallel-line count, description, delete). Shared by the part Connections tab
 *  and the polygon/ellipse connections list. */
/** Make a live-editing field undoable: snapshot the drawing when it gains focus,
 *  record that snapshot once on blur if the drawing changed. The element keeps its
 *  own `input` handler that mutates live for immediate feedback, so a whole typed
 *  edit collapses into a single, correctly-reverting undo step. */
export function undoableLiveEdit(el: HTMLInputElement | HTMLTextAreaElement): void {
  el.addEventListener('focus', () => beginGesture());
  el.addEventListener('blur', () => commitGesture());
}

/** Connections whose line settings are unfolded in their card — kept across a
 *  panel re-render, so editing one does not fold it shut again. */
const connLineOpen = new Set<string>();

/**
 * One connection card (the part's Connections tab, and the selected connection's
 * panel). It reads as a sentence: WHAT it is (schedule number + discipline, its
 * length), WHAT it joins (From / To, full names — they wrap, never ellipsised) and
 * WHAT it carries (the description, full width). How the line is drawn — parallel
 * lines, thickness, style, routing — is folded under one summary line.
 */
function connectionCard(c: Connection, nums: Map<string, string>, opts: { ends?: boolean } = {}): HTMLElement {
  const type = connDiscipline(c);
  const block = document.createElement('div');
  block.className = 'conn-card';
  block.style.borderLeftColor = DISCIPLINE_COLORS[type];

  const head = document.createElement('div');
  head.className = 'conn-head';
  const numBadge = document.createElement('span');
  numBadge.className = 'conn-num';
  numBadge.style.color = DISCIPLINE_COLORS[type];
  numBadge.textContent = nums.get(c.id) ?? '—';
  // The next connection drawn inherits the discipline set last (as sheets do).
  const disc = disciplineSelect(type, (v) => { pushHistory(); connEdit(c, { kind: 'discipline', type: v }); connectionUi.lastConnType = v; renderProperties(); invalidate(); }, false);
  disc.classList.add('conn-discipline');
  disc.title = tr('Discipline');
  const lenBadge = document.createElement('span');
  lenBadge.className = 'conn-len';
  lenBadge.textContent = `${(connectionRunLength(state.drawing, c) / 1000).toFixed(2)} m`;
  lenBadge.title = tr('Routed length of this connection');
  const del = document.createElement('button');
  del.className = 'del mini';
  del.textContent = '✕';
  del.title = tr('Delete connection');
  del.addEventListener('click', (e) => { e.stopPropagation(); removeConnection(c); });
  head.append(numBadge, disc, lenBadge, del);
  block.appendChild(head);
  block.appendChild(connCableFields(c));

  if (opts.ends !== false) {
    const ends = document.createElement('div');
    ends.className = 'conn-ends';
    for (const [label, which] of [[tr('From'), 'from'], [tr('To'), 'to']] as const) {
      const row = document.createElement('div');
      row.className = 'conn-end';
      const l = document.createElement('span');
      l.className = 'conn-end-label';
      l.textContent = label;
      const v = document.createElement('span');
      v.className = 'conn-end-name';
      v.textContent = connEndName(c, which);
      row.append(l, v);
      ends.appendChild(row);
    }
    block.appendChild(ends);
  }

  const desc = document.createElement('textarea');
  desc.className = 'conn-card-desc';
  desc.rows = 1;
  desc.placeholder = tr('What does it carry? e.g. 2 × power cable 4 × 0.75 mm²');
  desc.value = labelShown(c);
  const grow = (): void => { desc.style.height = 'auto'; desc.style.height = `${desc.scrollHeight}px`; };
  desc.addEventListener('input', () => {
    connEdit(c, { kind: 'label', text: desc.value });
    grow();
    invalidate();
  });
  undoableLiveEdit(desc);
  requestAnimationFrame(grow);
  block.appendChild(desc);

  if (c.pipe) { pointsAtConnection(block, c); return block; } // drawn as its pipe: no line settings
  const line = document.createElement('details');
  line.className = 'conn-line';
  line.open = connLineOpen.has(c.id);
  line.addEventListener('toggle', () => { if (line.open) connLineOpen.add(c.id); else connLineOpen.delete(c.id); });
  const summary = document.createElement('summary');
  const styleLabels: Record<string, string> = { solid: tr('Solid'), dashed: tr('Dashed'), dotted: tr('Dotted'), 'dash-dot': tr('Line-dot') };
  const paintSummary = (): void => {
    const n = c.lines;
    summary.textContent = [
      n === 1 ? tr('1 line') : tr('{n} parallel lines').replace('{n}', String(n)),
      styleLabels[c.style ?? 'dashed'],
      c.route === 'orthogonal' ? tr('Right-angle') : tr('Straight'),
    ].join(' · ');
  };
  paintSummary();
  const fields = document.createElement('div');
  fields.className = 'conn-fields';
  const lines = document.createElement('input');
  lines.type = 'number';
  lines.min = '1';
  lines.value = String(c.lines);
  lines.addEventListener('input', () => { connEdit(c, { kind: 'lines', n: parseInt(lines.value) || 1 }); paintSummary(); invalidate(); });
  undoableLiveEdit(lines);
  const thick = document.createElement('input');
  thick.type = 'number';
  thick.min = '0.5';
  thick.step = '0.5';
  thick.value = String(c.thickness ?? CONN_DEFAULTS.thickness);
  thick.addEventListener('input', () => { connEdit(c, { kind: 'thickness', px: parseFloat(thick.value) || CONN_DEFAULTS.thickness }); invalidate(); });
  undoableLiveEdit(thick);
  const style = document.createElement('select');
  for (const val of ['solid', 'dashed', 'dotted', 'dash-dot'] as const) {
    const o = document.createElement('option');
    o.value = val;
    o.textContent = styleLabels[val];
    style.appendChild(o);
  }
  style.value = c.style ?? 'dashed';
  style.addEventListener('change', () => { pushHistory(); connEdit(c, { kind: 'style', style: style.value as NonNullable<Connection['style']> }); paintSummary(); invalidate(); });
  const routing = document.createElement('select');
  routing.className = 'conn-routing';
  for (const [val, label] of [['straight', tr('Straight')], ['orthogonal', tr('Right-angle')]] as const) {
    const o = document.createElement('option');
    o.value = val;
    o.textContent = label;
    routing.appendChild(o);
  }
  routing.value = c.route ?? 'straight';
  routing.addEventListener('change', () => {
    pushHistory();
    connEdit(c, { kind: 'route', route: routing.value === 'orthogonal' ? 'orthogonal' : 'straight' });
    connectionUi.lastConnRoute = routing.value === 'orthogonal' ? 'orthogonal' : 'straight';
    renderProperties();
    invalidate();
  });
  fields.append(
    labeledField('Parallel lines', lines),
    labeledField('Thickness', thick),
    labeledField('Style', style),
    labeledField('Routing', routing),
  );
  line.append(summary, fields);
  block.appendChild(line);
  pointsAtConnection(block, c);
  return block;
}

/** A connection's cable, for the electrical schematic and the connection table:
 *  who lays it, and its length — the worked-out one (`connectionLength`) shown
 *  greyed until a length is typed; clearing the typed one goes back to it. */
function connCableFields(c: Connection): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'conn-fields conn-cable';
  const by = document.createElement('select');
  by.className = 'conn-installed-by';
  for (const [val, label] of [['stenal', tr('Stenal')], ['client', tr('Client')]] as const) {
    const o = document.createElement('option');
    o.value = val;
    o.textContent = label;
    by.appendChild(o);
  }
  by.value = c.installedBy ?? 'stenal';
  by.addEventListener('change', () => {
    pushHistory();
    connEdit(c, { kind: 'installedBy', by: by.value === 'client' ? 'client' : 'stenal' });
    renderProperties();
    invalidate();
  });
  const len = document.createElement('input');
  len.type = 'text';
  len.inputMode = 'decimal';
  len.className = 'conn-cable-length';
  const shown = (): string => (isComputedLength(c) ? '' : String(Math.round(c.length!)));
  len.value = shown();
  len.placeholder = String(connectionLength(state.drawing, { ...c, length: undefined }));
  len.title = tr('Worked out from its run as 3D draws it: up from each end to the run height, along it and down, + 20 %, rounded up to the metre; a pipe’s own length, exact. Type a length to replace it; clear it to go back.');
  len.addEventListener('change', () => {
    const raw = len.value.trim();
    if (!raw) {
      if (isComputedLength(c)) return;
      pushHistory();
      connEdit(c, { kind: 'length', mm: null });
    } else {
      const edit: ConnEdit = { kind: 'length', mm: parseMeasure(raw, 'mm') ?? Number.NaN };
      if (connEditRefusal({ drawing: state.drawing }, c, edit)) { flashStatus(tr('Not a length: type it in mm, or with a unit (e.g. 12 m).')); len.value = shown(); return; }
      pushHistory();
      connEdit(c, edit);
    }
    len.value = shown(); // as stored, even while the panel keeps its focus
    renderProperties();
    invalidate();
  });
  wrap.append(labeledField('Installed by', by), labeledField('Cable length (mm)', len));
  return wrap;
}

/** Delete one connection (a panel row's ✕ / row menu). */
function removeConnection(c: Connection): void {
  pushHistory();
  state.drawing.connections = state.drawing.connections.filter((x) => x !== c);
  if (state.selectedConnId === c.id) state.selectedConnId = null;
  setCanvasHover(null);
  renderProperties();
  invalidate();
}

/** A panel row that names a connection points at it: a click selects it, hovering
 *  outlines its route on the plan, a right-click offers Zoom to / Delete. */
function pointsAtConnection(el: HTMLElement, c: Connection): void {
  el.classList.add('conn-pick');
  const ref: ObjRef = { kind: 'conn', id: c.id };
  el.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('textarea, input, select, button, summary, details')) return;
    if (state.selectedConnId !== c.id) selectRef(ref);
  });
  el.addEventListener('mouseenter', () => { const o = refOutline(ref, { x: 0, y: 0 }); setCanvasHover(o ? { key: refKey(ref), ...o } : null); invalidate(); });
  el.addEventListener('mouseleave', () => { setCanvasHover(null); invalidate(); });
  el.addEventListener('contextmenu', (e) => {
    if ((e.target as HTMLElement).closest('textarea, input')) return;
    e.preventDefault();
    showObjectRowMenu(e, { zoomTo: () => { const p = connectionPath(c); if (p) zoomToPoints(p); }, del: () => removeConnection(c), lockId: c.id });
  });
}

/** The panel of a SELECTED connection: where it runs from and to, then the same
 *  card the Connections tab shows (discipline, lines, width, style, description). */
export function renderConnectionPanel(body: HTMLElement, c: Connection): void {
  body.appendChild(sectionTitle('Connection'));
  body.appendChild(infoRow('From', connEndName(c, 'from')));
  body.appendChild(infoRow('To', connEndName(c, 'to')));
  body.appendChild(buttonRow('Reverse direction', () => reverseSelectedConnection(c)));
  body.appendChild(connectionCard(c, scheduleNumbers(connectionSchedule(state.drawing)), { ends: false }));
  renderPipeRows(body, c);
  if (!c.pipe) renderConn3dRows(body, c);
}

/** Connections list for a selected polygon/ellipse: the connections touching it,
 *  plus a control to link it to any other part, polygon or ellipse. */
export function renderPolyConnections(body: HTMLElement, poly: Polygon): void {
  body.appendChild(sectionTitle('Connections'));
  const conns = state.drawing.connections.filter((c) => c.from === poly.id || c.to === poly.id);
  const nums = scheduleNumbers(connectionSchedule(state.drawing));
  if (conns.length === 0) {
    const e = document.createElement('div');
    e.className = 'prop-empty';
    e.textContent = tr('No connections to or from this shape yet.');
    body.appendChild(e);
  }
  for (const c of conns) body.appendChild(connectionCard(c, nums));

  // Add a connection to any other object an end can be glued to (a room's own
  // are its doors and heater walls: the Connect tool's).
  const targets = connHostObjects(state.drawing)
    .filter((h) => h.obj.id !== poly.id && !(h.kind === 'instance' && (h.obj as ProductInstance).autoBenches))
    .map((h) => ({ id: h.obj.id, label: connectionEndpointName(h.obj.id) }));
  if (!targets.length) return;
  const addRow = document.createElement('div');
  addRow.className = 'conn-add';
  const sel = document.createElement('select');
  for (const t of targets) {
    const opt = document.createElement('option');
    opt.value = t.id;
    opt.textContent = `→ ${t.label}`;
    sel.appendChild(opt);
  }
  const add = document.createElement('button');
  add.className = 'mini';
  add.textContent = tr('＋ Add connection');
  add.addEventListener('click', () => {
    if (!sel.value) return;
    addConnection({ id: newConnectionId(), from: poly.id, to: sel.value });
  });
  addRow.append(sel, add);
  body.appendChild(addRow);
}

/** The parts a part's Connections dropdown offers: those related to it, those
 *  standing in the same room and the other parts of its own group (product). */
function connectableParts(inst: ProductInstance): ProductInstance[] {
  if (inst.autoBenches) return [];
  const host = hostRoomOf(inst);
  const related = new Set(relatedTo(state.drawing.instances, inst));
  return state.drawing.instances.filter((m) => m !== inst && !m.autoBenches
    && (related.has(m) || (!!inst.groupId && m.groupId === inst.groupId) || (!!host && hostRoomOf(m) === host)));
}

/** Connections tab: connections to/from the selected part, with an add control. */
export function renderConnectionsTab(body: HTMLElement, inst: ProductInstance): void {
  body.appendChild(sectionTitle('Connections'));
  // Only connections that touch the selected part.
  const conns = state.drawing.connections.filter((c) => c.from === inst.id || c.to === inst.id);
  const nums = scheduleNumbers(connectionSchedule(state.drawing)); // E1 / P1 / M1 …
  if (conns.length === 0) {
    const e = document.createElement('div');
    e.className = 'prop-empty';
    e.textContent = tr('No connections to or from this part yet.');
    body.appendChild(e);
  }
  for (const c of conns) body.appendChild(connectionCard(c, nums));
  // Add a connection from this part to a part it relates to (the connection tool
  // joins any two parts).
  const others = connectableParts(inst);
  if (others.length && !inst.autoBenches) {
    const addRow = document.createElement('div');
    addRow.className = 'conn-add';
    const sel = document.createElement('select');
    for (const m of others) {
      const opt = document.createElement('option');
      opt.value = m.id;
      opt.textContent = `→ ${numberedInstanceNames().get(m.id) ?? productName(m, getUiLang())}`;
      sel.appendChild(opt);
    }
    const add = document.createElement('button');
    add.className = 'mini';
    add.textContent = tr('＋ Add connection');
    add.addEventListener('click', () => {
      addConnection({ id: newConnectionId(), from: inst.id, to: sel.value });
    });
    addRow.append(sel, add);
    body.appendChild(addRow);
  } else if (!inst.autoBenches) {
    const e = document.createElement('div');
    e.className = 'prop-empty';
    e.textContent = tr('Use Connect (type CONNECT) to draw a connection from this part to another object or point.');
    body.appendChild(e);
  }
}

/** Connections tab with NOTHING selected: the whole project's connection schedule
 *  as a table (No. / From / To / Description), like the in-drawing schedule table.
 *  Each description is a multiline cell you can click into and edit; the other
 *  columns are read-only here. */
export function renderAllConnections(body: HTMLElement): void {
  body.appendChild(sectionTitle('Connections'));
  const rows = connectionRows(state.drawing);
  if (rows.length === 0) {
    const e = document.createElement('div');
    e.className = 'prop-empty';
    e.textContent = tr('No connections in this project yet.');
    body.appendChild(e);
    return;
  }
  const s = scheduleStrings(state.drawing.language ?? 'it');
  const table = document.createElement('table');
  table.className = 'conn-table';
  const thead = document.createElement('thead');
  const htr = document.createElement('tr');
  for (const h of [s.no, s.description]) {
    const th = document.createElement('th'); th.textContent = h; htr.appendChild(th);
  }
  thead.appendChild(htr); table.appendChild(thead);
  const tbody = document.createElement('tbody');
  for (const r of rows) {
    const tr2 = document.createElement('tr');
    const numTd = document.createElement('td');
    numTd.className = 'conn-num';
    numTd.textContent = r.num;
    numTd.style.color = CONNECTION_COLORS[r.type] ?? '#cdd6e0';
    const descTd = document.createElement('td');
    const ta = document.createElement('textarea');
    ta.className = 'conn-desc';
    ta.value = r.description;
    ta.rows = Math.max(1, r.description.split('\n').length);
    const grow = (): void => { ta.style.height = 'auto'; ta.style.height = `${ta.scrollHeight}px`; };
    // beginGesture snapshots the pre-edit state; the panel's global focusout
    // commits it — one undo step per edit, matching the other panel fields.
    ta.addEventListener('focus', () => { beginGesture(); grow(); });
    ta.addEventListener('input', () => {
      const c = state.drawing.connections.find((x) => x.id === r.id);
      if (c) connEdit(c, { kind: 'label', text: ta.value });
      grow();
      invalidate();
    });
    descTd.appendChild(ta);
    tr2.append(numTd, descTd);
    const conn = state.drawing.connections.find((x) => x.id === r.id);
    if (conn) pointsAtConnection(tr2, conn);
    tbody.appendChild(tr2);
  }
  table.appendChild(tbody);
  body.appendChild(table);
}
